/**
 * LERA Smart Search — Bootstrap (Sprint A)
 *
 * Sole responsibility: connect the header search inputs to the LERA Device
 * Engine with lazy loading. No search logic, no UI, no DOM changes.
 *
 * - The Device Engine assets are NOT loaded on page load.
 * - First focus / click / keydown on a header search input triggers loading.
 * - Engine modules are injected in dependency order, then initialized once.
 * - The search form keeps its native behavior (GET /search) untouched.
 *
 * Public entry point (no business logic yet — future sprints build on it):
 *   window.LERA.smartSearch = { state, ensure(), getEngine(), isReady() }
 *
 * Events on document: lera:smart-search:loading | ready | error
 */
(function (global) {
  'use strict';

  var LERA = (global.LERA = global.LERA || {});
  if (LERA.smartSearch) return;

  var STATES = {
    IDLE: 'idle',
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
  };

  /** Header search inputs to observe. LERA forms only — Dawn modal untouched. */
  var INPUT_SELECTOR = '.lera-search-form input[type="search"]';
  var ACTIVATION_EVENTS = ['focusin', 'click', 'keydown'];

  var scriptEl = document.currentScript;
  var config = {
    engineAssets: [],
    optionName: 'Dispositivo compatible',
    cacheEnabled: true,
  };

  if (scriptEl) {
    var rawAssets = scriptEl.getAttribute('data-engine-assets') || '';
    config.engineAssets = rawAssets
      .split(',')
      .map(function (url) {
        return url.trim();
      })
      .filter(Boolean);
    config.optionName = scriptEl.getAttribute('data-option-name') || config.optionName;
  }

  var state = STATES.IDLE;
  var ensurePromise = null;
  var listenersBound = false;

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent('lera:smart-search:' + name, { detail: detail || null }));
    } catch (e) {
      /* noop */
    }
  }

  function setState(next, detail) {
    state = next;
    api.state = next;
    emit(next === STATES.READY ? 'ready' : next === STATES.ERROR ? 'error' : 'loading', detail);
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.leraLoaded === '1') return resolve();
        existing.addEventListener('load', function () {
          resolve();
        });
        existing.addEventListener('error', reject);
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserve execution order among injected modules
      script.addEventListener('load', function () {
        script.dataset.leraLoaded = '1';
        resolve();
      });
      script.addEventListener('error', function () {
        reject(new Error('Failed to load ' + src));
      });
      document.head.appendChild(script);
    });
  }

  function loadEngineModules() {
    // Engine already present (e.g. Compatibility Finder section on this page).
    if (LERA.compatibility) return Promise.resolve();

    if (!config.engineAssets.length) {
      return Promise.reject(new Error('No engine assets configured on bootstrap script tag.'));
    }

    return config.engineAssets.reduce(function (chain, src) {
      return chain.then(function () {
        return loadScript(src);
      });
    }, Promise.resolve());
  }

  function initializeEngine() {
    var engine = LERA.compatibility;
    if (!engine || typeof engine.init !== 'function') {
      throw new Error('Device Engine unavailable after load.');
    }

    // If another consumer (Compatibility Finder) already initialized the
    // engine, reuse it as-is — never re-init and reset its pipeline.
    var diagnostics = null;
    try {
      diagnostics = typeof engine.getDiagnostics === 'function' ? engine.getDiagnostics() : null;
    } catch (e) {
      diagnostics = null;
    }

    if (diagnostics && diagnostics.initialized) {
      return engine;
    }

    engine.init({
      optionName: config.optionName,
      catalogSource: 'global',
      cacheEnabled: config.cacheEnabled,
      enableSessionContext: false,
      enableDiagnostics: false,
      debugMode: false,
      customAliases: '',
      brands: [],
      messages: {},
    });

    return engine;
  }

  function ensure() {
    if (ensurePromise) return ensurePromise;

    setState(STATES.LOADING);

    ensurePromise = loadEngineModules()
      .then(function () {
        var engine = initializeEngine();
        setState(STATES.READY, { engine: true });
        return engine;
      })
      .catch(function (error) {
        ensurePromise = null; // allow retry on next interaction
        setState(STATES.ERROR, { message: error && error.message });
        return null;
      });

    return ensurePromise;
  }

  function onFirstInteraction(event) {
    var target = event.target;
    if (!target || typeof target.matches !== 'function') return;
    if (!target.matches(INPUT_SELECTOR)) return;
    unbindActivationListeners();
    ensure();
  }

  function bindActivationListeners() {
    if (listenersBound) return;
    listenersBound = true;
    ACTIVATION_EVENTS.forEach(function (name) {
      document.addEventListener(name, onFirstInteraction, { passive: true, capture: true });
    });
  }

  function unbindActivationListeners() {
    if (!listenersBound) return;
    listenersBound = false;
    ACTIVATION_EVENTS.forEach(function (name) {
      document.removeEventListener(name, onFirstInteraction, { capture: true });
    });
  }

  var api = {
    version: 'A.1',
    state: state,
    ensure: ensure,
    isReady: function () {
      return state === STATES.READY;
    },
    getEngine: function () {
      return state === STATES.READY ? LERA.compatibility || null : null;
    },
  };

  LERA.smartSearch = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindActivationListeners);
  } else {
    bindActivationListeners();
  }
})(typeof window !== 'undefined' ? window : this);
